import { getDuration } from '@common-fe/common-fe';
import { AxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { toNumber } from 'lodash';

import { Roles } from '@/common/constants';
import { MOBILE_POPUP_VISIBILITY_KEY } from '@/modules/core/components/ContinueInAppPopup/hooks/usePopupVisible';
import { VERIFICATION_LIMIT_KEY } from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/components/VerifyMyCardModal/VerifyMyCardModal.constants';
import { CIP_STORAGE_KEY } from '@/modules/investments/cip/hooks/useFailedCIPStorage';

import { clearLocalStorage } from './clearLocalStorage';

interface RefreshToken {
  'custom:permissions': string;
  'custom:role': Roles;
  'custom:person_id': string;
  'custom:employee_id': string;
  'custom:organization_id': string;
  'cognito:username': string;
}

const ACCESS_TOKEN_KEY = 'id_token';
const EXPIRES_IN_KEY = 'expires_in';
const TOKEN_REFRESH_BUFFER_SECONDS = 10;
export const SEEN_KEY = 'seen';
export const IS_SSO_TOKEN = 'IS_SSO_TOKEN';

interface StringMap {
  [key: string]: string;
}
const EXCEPT_CLEAR_KEYS = [VERIFICATION_LIMIT_KEY, CIP_STORAGE_KEY, MOBILE_POPUP_VISIBILITY_KEY];

type TokenListener = () => void;
const tokenListeners: TokenListener[] = [];

export const subscribeOnTokenChange = (cb: TokenListener) => {
  tokenListeners.push(cb);
  return () => {
    const index = tokenListeners.indexOf(cb);
    if (index !== -1) tokenListeners.splice(index, 1);
  };
};

const notifyTokenChange = () => {
  for (const listener of tokenListeners) {
    listener();
  }
};

export default class Token {
  private static readonly LOCAL_STORAGE_TOKEN = 'token';

  private static readonly LOCAL_STORAGE_REFRESH_TOKEN = 'refresh_token';

  private static readonly LOCAL_STORAGE_EXPIRES_IN = 'expires_in';
  private static readonly LOCAL_STORAGE_EXPIRES_IN_TIMESTAMP = 'expires_in_timestamp';

  private static readonly LOCAL_STORAGE_SSO_EXPIRES_IN = 'sso_expires_in';
  private static readonly LOCAL_STORAGE_SSO_EXPIRES_IN_TIMESTAMP = 'sso_expires_in_timestamp';

  private static readonly LOCAL_USER_NAME = 'user_name';

  public static isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  public static getAuthentication(): AxiosRequestConfig {
    const token = this.getToken();
    if (!token) {
      return {};
    }
    return {
      headers: { Authorization: `Bearer ${token}` },
    };
  }

  public static storeToken(token: string): void {
    localStorage.setItem(Token.LOCAL_STORAGE_TOKEN, token);
    notifyTokenChange();
  }

  public static storeRefreshToken(refreshToken: string): void {
    localStorage.setItem(Token.LOCAL_STORAGE_REFRESH_TOKEN, refreshToken);
  }

  public static storeExpiresIn(expiresIn: number): void {
    localStorage.setItem(
      Token.LOCAL_STORAGE_EXPIRES_IN_TIMESTAMP,
      `${Date.now() + getDuration({ seconds: toNumber(expiresIn - TOKEN_REFRESH_BUFFER_SECONDS) })}`
    );
    localStorage.setItem(Token.LOCAL_STORAGE_EXPIRES_IN, `${expiresIn}`);
  }

  public static storeSsoExpiresIn(expiresIn: number): void {
    localStorage.setItem(
      Token.LOCAL_STORAGE_SSO_EXPIRES_IN_TIMESTAMP,
      `${Date.now() + getDuration({ seconds: toNumber(expiresIn - TOKEN_REFRESH_BUFFER_SECONDS) })}`
    );
    localStorage.setItem(Token.LOCAL_STORAGE_SSO_EXPIRES_IN, `${expiresIn}`);
  }

  public static storeUserName(userName: string): void {
    localStorage.setItem(Token.LOCAL_USER_NAME, userName);
  }

  public static clear(): void {
    const savedValues = EXCEPT_CLEAR_KEYS.reduce((result: StringMap, item) => {
      const value = localStorage.getItem(item);
      return {
        ...result,
        [item]: value || '',
      };
    }, {});

    clearLocalStorage();
    localStorage.removeItem(Token.LOCAL_STORAGE_TOKEN);
    localStorage.removeItem(Token.LOCAL_STORAGE_REFRESH_TOKEN);
    localStorage.setItem(SEEN_KEY, 'true');
    Object.keys(savedValues).forEach((key) => {
      if (savedValues[key]) {
        localStorage.setItem(key, savedValues[key] || '');
      }
    });
  }

  public static getRefreshToken(): string | null {
    return localStorage.getItem(Token.LOCAL_STORAGE_REFRESH_TOKEN);
  }

  public static getUserName(): string | null {
    return localStorage.getItem(Token.LOCAL_USER_NAME);
  }

  public static getToken() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    let accessToken = urlParams.get(ACCESS_TOKEN_KEY);
    let expiresIn;

    const { hash, search, pathname } = window.location;
    if (!accessToken) {
      const accessTokenPart = hash.split('&').find((part) => part.startsWith(ACCESS_TOKEN_KEY)) || '';
      const expiresInPart = hash.split('&').find((part) => part.startsWith(EXPIRES_IN_KEY)) || '';

      accessToken = accessTokenPart.split('=')[1];
      expiresIn = expiresInPart.split('=')[1];
    }
    if (accessToken) {
      localStorage.setItem(Token.LOCAL_STORAGE_TOKEN, accessToken);
      if (expiresIn) {
        Token.storeSsoExpiresIn(toNumber(expiresIn));
      }
      localStorage.setItem(IS_SSO_TOKEN, 'true');
      window.location.replace(pathname + search);
      return accessToken;
    }
    return localStorage.getItem(Token.LOCAL_STORAGE_TOKEN);
  }

  public static isExpired(): boolean {
    const expiresAt = localStorage.getItem(Token.LOCAL_STORAGE_EXPIRES_IN_TIMESTAMP);
    return expiresAt ? Date.now() >= Number(expiresAt) : false;
  }

  public static isSsoExpired(): boolean {
    const expiresAt = localStorage.getItem(Token.LOCAL_STORAGE_SSO_EXPIRES_IN_TIMESTAMP);
    return expiresAt ? Date.now() >= Number(expiresAt) : false;
  }

  public static setSSOToken(token?: string, expiresIn?: string) {
    if (token) {
      localStorage.setItem(Token.LOCAL_STORAGE_TOKEN, token);
      localStorage.setItem(IS_SSO_TOKEN, 'true');
    }
    if (expiresIn) {
      Token.storeSsoExpiresIn(toNumber(expiresIn));
    }
  }

  public static getSSODataFromHash() {
    const { hash } = window.location;
    const accessTokenPart = hash.split('&').find((part) => part.startsWith(ACCESS_TOKEN_KEY)) || '';
    const accessToken = accessTokenPart.split('=')[1];
    const expiresInPart =
      hash.split('&').find((part) => part.startsWith(Token.LOCAL_STORAGE_EXPIRES_IN)) || '';
    const expiresIn = expiresInPart.split('=')[1];
    return accessToken ? { accessToken, expiresIn } : undefined;
  }

  public static getDecodedToken(): RefreshToken | null {
    const token = Token.getToken();
    if (!token) {
      return null;
    }
    return jwtDecode(token);
  }
}
